import * as React from "react";

import { useLazyApi } from "../../../hooks/useApi/useApi";
import * as proxy from "../../../services/api/proxy";
import { useCompanyInfo } from "../CompanyInfoContext/CompanyInfoContext";
import { createPricePlanFeatureContextValue, PricePlanFeatureContext } from "./PricePlanFeatureContext";

export const PricePlanFeatureContextProvider: React.FC<React.PropsWithChildren> = props => {
	const { companyInfo } = useCompanyInfo();
	// We need a null check here because the company info is not always available when this component is mounted
	// even if the companyInfo "can't" be undefined if you look at the type.
	const companyId = companyInfo?.Id;
	const [pricePlanFeaturesApi, pricePlanFeaturesRequest] = useLazyApi(
		proxy.Bokio.Common.Web.Plan.PricePlanFeatureController.GetAllFeaturesAvailableForCompany.Get,
	);
	const [getPriceplanState, priceplanStateRequest] = useLazyApi(
		proxy.Bokio.Common.Web.Settings.SubscriptionController.PriceplanState.Get,
	);

	const refreshPricePlanFeatures = React.useCallback(async () => {
		if (companyId) {
			pricePlanFeaturesApi(companyId);
			getPriceplanState(companyId);
		}
	}, [companyId, pricePlanFeaturesApi, getPriceplanState]);
	React.useEffect(() => {
		if (companyId) {
			pricePlanFeaturesApi(companyId);
			getPriceplanState(companyId);
		}
	}, [companyId, pricePlanFeaturesApi, getPriceplanState]);

	const pricePlanFeatures = React.useMemo(
		() => (pricePlanFeaturesRequest.data?.Data ? pricePlanFeaturesRequest.data.Data : []),
		[pricePlanFeaturesRequest.data],
	);

	const currentPlanAddons = React.useMemo(
		() => priceplanStateRequest.data?.Data?.CurrentPlan?.AppliedAddons ?? [],
		[priceplanStateRequest.data?.Data?.CurrentPlan?.AppliedAddons],
	);

	const contextValue = React.useMemo(
		() =>
			createPricePlanFeatureContextValue({
				refreshPricePlanFeatures,
				pricePlanFeatures,
				currentPlanAddons,
				isLoading: pricePlanFeaturesRequest.isLoading || priceplanStateRequest.isLoading,
			}),
		[
			refreshPricePlanFeatures,
			pricePlanFeatures,
			currentPlanAddons,
			pricePlanFeaturesRequest.isLoading,
			priceplanStateRequest.isLoading,
		],
	);

	return <PricePlanFeatureContext value={contextValue}>{props.children}</PricePlanFeatureContext>;
};
